
import { Quadrant, Task, AnalyticsMetrics } from "../types";
import { getAuthHeaders } from "./firebaseService";

// All AI calls are proxied through the server to keep API keys secure.
// No API keys are stored or used on the client side.

async function aiPost(endpoint: string, body: Record<string, any>): Promise<any> {
  const headers = await getAuthHeaders();
  const res = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'AI service unavailable' }));
    throw new Error(err.error || `AI request failed (${res.status})`);
  }
  return res.json();
}

async function callWithRetry<T>(fn: () => Promise<T>, retries = 3, delay = 1000): Promise<T> {
  try {
    return await fn();
  } catch (error: any) {
    const errorStr = error?.message || "";
    const isRetryable = errorStr.includes('429') || errorStr.includes('RESOURCE_EXHAUSTED') || errorStr.includes('500');
    if (isRetryable && retries > 0) {
      console.warn(`[Gemini] Retrying in ${delay}ms... (${retries} left)`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return callWithRetry(fn, retries - 1, delay * 2);
    }
    throw error;
  }
}

export const geminiService = {
  async generateVentureAudit(metrics: AnalyticsMetrics, tasks: Task[]): Promise<string> {
    return callWithRetry(async () => {
      const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
      const recentWins = tasks
        .filter(t => (t.isCompleted || t.status === 'COMPLETED') && (t.completedAt || 0) >= twentyFourHoursAgo)
        .map(t => t.title);
      const strategicObjectives = tasks
        .filter(t => !t.isCompleted && t.currentQuadrant === Quadrant.SCHEDULE)
        .slice(0, 3)
        .map(t => t.title);

      const data = await aiPost('/api/ai/coach-insight', { metrics, recentWins, strategicObjectives });
      return data.text || "Your execution remains steady. Rest and return with intent.";
    });
  },

  async processVoiceToTask(transcript: string): Promise<{ title: string; quadrant: Quadrant; description: string }> {
    return callWithRetry(async () => {
      const data = await aiPost('/api/ai/voice-to-task', { transcript });
      return {
        title: data.title || transcript,
        quadrant: (data.quadrant as Quadrant) || Quadrant.DO,
        description: data.description || ''
      };
    }).catch(error => {
      console.error("Processing Error:", error);
      return { title: transcript, quadrant: Quadrant.DO, description: '' };
    });
  },

  async suggestSubtasks(taskTitle: string, taskDescription?: string): Promise<string[]> {
    return callWithRetry(async () => {
      const data = await aiPost('/api/ai/suggest-subtasks', { taskTitle, taskDescription });
      return data.subtasks || [];
    }).catch(error => {
      console.error("Breakdown Error:", error);
      return [];
    });
  },

  async generatePulseDraft(taskTitle: string): Promise<string> {
    return callWithRetry(async () => {
      const data = await aiPost('/api/ai/pulse-draft', { taskTitle });
      return data.text || `Status update for: ${taskTitle}. Proceeding with intentional focus.`;
    }).catch(error => {
      console.error("Drafting Error:", error);
      return "Status update requested.";
    });
  }
};
